/* eslint-disable react-hooks/exhaustive-deps */
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Paper,
	styled,
	Table,
	TableBody,
	TableContainer,
	TablePagination,
} from "@mui/material";
import { api, types } from "api";
import { InfoSnackBar } from "components/InfoSnackBar";
import { getComparator, Order, stableSort } from "helpers/tableComparator";
import { DELETE_FAIL } from "messages";
import React, { useEffect, useState } from "react";
import { ViewGroupsPageTableEditGroup } from "./ViewGroupsPageTableEditGroup";
import { ViewGroupsPageTableHead } from "./ViewGroupsPageTableHead";
import { ViewGroupsPageTableRow } from "./ViewGroupsPageTableRow";
import { ViewGroupsPageTableToolbar } from "./ViewGroupsPageTableToolbar";

const PREFIX = "ViewGroupsPageTable";

const classes = {
	root: `${PREFIX}-root`,
	paper: `${PREFIX}-paper`,
	table: `${PREFIX}-table`,
	hide: `${PREFIX}-hide`,
	button: `${PREFIX}-button`,
};

const Root = styled("div")(({ theme }) => ({
	[`&.${classes.root}`]: {
		width: "100%",
	},

	[`& .${classes.paper}`]: {
		width: "100%",
		marginBottom: theme.spacing(2),
	},

	[`& .${classes.table}`]: {
		minWidth: 750,
	},

	[`& .${classes.hide}`]: {
		display: "none",
	},

	[`& .${classes.button}`]: {
		float: "left",
		margin: "10",
	},
}));

export interface ViewGroupsPageTableProps {
	group: types.Group;
	update: (groups: types.Group) => void;
	// Used to delete groups from the parent list of groups
	deleteGroupFromList: (group: types.Group) => void;
}
export const ViewGroupsPageTable = ({
	group,
	update,
	deleteGroupFromList,
}: ViewGroupsPageTableProps): JSX.Element => {
	const [order, setOrder] = useState<Order>("asc");
	const [orderBy, setOrderBy] = useState<keyof types.UserBasic>("firstName");
	const [selected, setSelected] = useState<string[]>([]);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const [newGroup, setGroup] = useState<types.Group>(group);
	const [open, setOpen] = useState(false);
	const [msg, setMsg] = useState("");
	const [deleteID, setDeleteID] = useState<string[]>([]);
	const [err, setErr] = useState<boolean>(false);

	const handleRequestSort = (
		event: React.MouseEvent<unknown>,
		property: keyof types.UserBasic,
	): void => {
		const isAsc = orderBy === property && order === "asc";
		setOrder(isAsc ? "desc" : "asc");
		setOrderBy(property);
	};

	const handleSelectAllClick = (
		event: React.ChangeEvent<HTMLInputElement>,
	): void => {
		if (event.target.checked) {
			const newSelecteds = group.students.map((n) => n.userId);
			setSelected(newSelecteds);
			return;
		}
		setSelected([]);
	};

	const handleClick = (
		event: React.MouseEvent<unknown>,
		userid: string,
	): void => {
		const selectedIndex = selected.indexOf(userid);
		let newSelected: string[] = [];

		if (selectedIndex === -1) {
			// If the row does not been checked
			newSelected = [...selected, userid];
		} else if (selectedIndex === 0) {
			// If the row's selected record is at the begin of the list
			newSelected = [...selected.slice(1)];
		} else if (selectedIndex === selected.length - 1) {
			// If the row's selected record is at the end of the list
			newSelected = [...selected.slice(0, -1)];
		} else if (selectedIndex > 0) {
			// Other case when the row is selected
			newSelected = [
				...selected.slice(0, selectedIndex),
				...selected.slice(selectedIndex + 1),
			];
		}

		setSelected(newSelected);
	};

	const handleChangePage = (event: unknown, newPage: number): void => {
		setPage(newPage);
	};

	// Open the dialog for deleting single student
	const handleDeleteOpen = (UUID: string): void => {
		setDeleteID([UUID]);
		setMsg("Do you want to delete this student?");
		setOpen(true);
	};

	const handleClose = (): void => {
		setDeleteID([]);
		setOpen(false);
	};

	// Handle delete student, will delete all students in the delete list.
	const handleDelete = (): void => {
		api
			.deleteUsersFromGroup(group.groupId, deleteID)
			.then((res) => {
				setErr(res.status !== 201);
				setOpen(false);
				setGroup({
					...newGroup,
					students: newGroup.students.filter(
						(item) => !deleteID.includes(item.userId),
					),
				});
			})
			.catch(() => {
				setErr(true);
				setOpen(false);
			});
	};

	// Open the dialog for deleting single student
	const handleDeleteMultipleOpen = (): void => {
		setMsg("Do you want to delete these student?");
		setDeleteID(selected);
		setOpen(true);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	): void => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	useEffect(() => {
		update(newGroup);
	}, [newGroup]);

	return (
		<Root className={classes.root}>
			<Paper className={classes.paper}>
				<ViewGroupsPageTableToolbar
					numSelected={selected.length}
					handleDelete={handleDeleteMultipleOpen}
				>
					<ViewGroupsPageTableEditGroup
						group={group}
						handler={setGroup}
						deleteGroupFromList={deleteGroupFromList}
					/>
				</ViewGroupsPageTableToolbar>
				<TableContainer>
					<Table
						className={classes.table}
						aria-labelledby="tableTitle"
						size="small"
						aria-label="enhanced table"
					>
						<ViewGroupsPageTableHead
							numSelected={selected.length}
							order={order}
							orderBy={orderBy}
							onSelectAllClick={handleSelectAllClick}
							onRequestSort={handleRequestSort}
							rowCount={group.students.length}
						/>
						<TableBody>
							{stableSort(group.students, getComparator(order, orderBy))
								.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
								.map((row, index) => {
									const labelId = `enhanced-table-checkbox-${index}`;

									return (
										<ViewGroupsPageTableRow
											selected={selected}
											handleClick={handleClick}
											handleDeleteOpen={handleDeleteOpen}
											row={row}
											key={row.email}
											labelId={labelId}
										/>
									);
								})}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					rowsPerPageOptions={[5, 10, 25]}
					component="div"
					count={group.students.length}
					rowsPerPage={rowsPerPage}
					page={page}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
				/>
			</Paper>
			<Dialog
				open={open}
				onClose={handleClose}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
			>
				<DialogTitle id="alert-dialog-title">Alert</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						{msg}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} color="primary">
						CANCEL
					</Button>
					<Button onClick={handleDelete} color="primary" autoFocus>
						OK
					</Button>
				</DialogActions>
			</Dialog>
			<InfoSnackBar
				isOpen={err}
				onClose={() => setErr(false)}
				severity="error"
				message={DELETE_FAIL}
			/>
		</Root>
	);
};
